import moment from 'moment';
import { toAmsterdam, amsterdamDateTime } from '../utils/time';

export interface BusyPeriod {
  id: number;
  bike_id: number;
  start_at: string;
  end_at: string;
  status: string;
}

export interface DaySelection {
  start: string;
  durationHours: number;
}

export interface BusinessHours {
  start: string;
  end: string;
}

export interface DayBlock {
  key: string;
  startLabel: string;
  endLabel: string;
  topPercent: number;
  heightPercent: number;
}

export interface HourMarker {
  key: string;
  label: string;
  topPercent: number;
}

export interface DayLayout {
  busyBlocks: DayBlock[];
  selectionBlock: DayBlock | null;
  hourMarkers: HourMarker[];
  trackHeight: number;
}

export const PIXELS_PER_HOUR = 56;

// Breathing room above and below the timeline so the first and last hour label,
// which are centred on the edge, are not clipped by the track.
export const TRACK_INSET = 12;

// A block never gets smaller than this, otherwise a short reservation would be
// an invisible hairline on the timeline.
const MIN_BLOCK_PERCENT = 2;

export const computeDayLayout = (
  date: string,
  businessHours: BusinessHours,
  busyPeriods: BusyPeriod[],
  selection?: DaySelection | null
): DayLayout => {
  const dayStart = amsterdamDateTime(date, 'YYYY-MM-DD').startOf('day');
  const dayEnd = dayStart.clone().endOf('day');

  // Reservations are clamped to the selected day so a booking that runs past
  // midnight still shows as a block ending at the bottom of the timeline.
  const periodsOnDay = busyPeriods
    .map(period => ({
      id: period.id,
      start: toAmsterdam(period.start_at),
      end: toAmsterdam(period.end_at)
    }))
    .filter(period => period.end.isAfter(dayStart) && period.start.isBefore(dayEnd))
    .map(period => ({
      id: period.id,
      start: period.start.isBefore(dayStart) ? dayStart.clone() : period.start,
      end: period.end.isAfter(dayEnd) ? dayEnd.clone() : period.end
    }))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  const selectionStart = selection?.start ? toAmsterdam(selection.start) : null;
  const selectionEnd = selectionStart
    ? selectionStart.clone().add(selection!.durationHours, 'hours')
    : null;
  const selectionOnDay =
    selectionStart && selectionEnd && selectionEnd.isAfter(dayStart) && selectionStart.isBefore(dayEnd)
      ? {
          start: selectionStart.isBefore(dayStart) ? dayStart.clone() : selectionStart,
          end: selectionEnd.isAfter(dayEnd) ? dayEnd.clone() : selectionEnd
        }
      : null;

  // The timeline normally covers the business hours, but widens when something
  // falls outside them so nothing is hidden from view.
  let windowStart = amsterdamDateTime(`${date} ${businessHours.start}`);
  let windowEnd = amsterdamDateTime(`${date} ${businessHours.end}`);

  const edges = [...periodsOnDay];
  if (selectionOnDay) {
    edges.push({ id: -1, ...selectionOnDay });
  }
  edges.forEach(edge => {
    if (edge.start.isBefore(windowStart)) windowStart = edge.start.clone();
    if (edge.end.isAfter(windowEnd)) windowEnd = edge.end.clone();
  });

  windowStart = windowStart.startOf('hour');
  if (windowEnd.minutes() > 0 || windowEnd.seconds() > 0) {
    windowEnd = windowEnd.startOf('hour').add(1, 'hour');
  }

  const windowMinutes = Math.max(windowEnd.diff(windowStart, 'minutes'), 60);

  const toPercent = (value: moment.Moment): number =>
    (value.diff(windowStart, 'minutes') / windowMinutes) * 100;

  const toBlock = (
    key: string,
    start: moment.Moment,
    end: moment.Moment
  ): DayBlock => {
    const topPercent = Math.min(Math.max(toPercent(start), 0), 100);
    const rawHeight = Math.min(toPercent(end), 100) - topPercent;
    return {
      key,
      startLabel: start.format('HH:mm'),
      endLabel: end.format('HH:mm'),
      topPercent,
      heightPercent: Math.max(rawHeight, MIN_BLOCK_PERCENT)
    };
  };

  const hourMarkers: HourMarker[] = [];
  const marker = windowStart.clone();
  while (marker.isSameOrBefore(windowEnd)) {
    hourMarkers.push({
      key: `hour-${marker.format('HH:mm')}`,
      label: marker.format('HH:mm'),
      topPercent: toPercent(marker)
    });
    marker.add(1, 'hour');
  }

  return {
    busyBlocks: periodsOnDay.map(period => toBlock(`busy-${period.id}`, period.start, period.end)),
    selectionBlock: selectionOnDay
      ? toBlock('selection', selectionOnDay.start, selectionOnDay.end)
      : null,
    hourMarkers,
    trackHeight: (windowMinutes / 60) * PIXELS_PER_HOUR + 2 * TRACK_INSET
  };
};
